import { motion } from 'framer-motion';
import { Shield, Activity, Wifi, Bell, Zap, Lock } from 'lucide-react';

const SolutionsPage = () => {
  const solutions = [
    {
      icon: Shield,
      title: 'Network Security Intelligence',
      description: 'Guardian agent analyzes DNS traffic in real-time, blocking threats before they reach your devices. 6,000+ threats blocked and counting.',
      features: [
        'Real-time DNS threat detection',
        'Automatic malicious domain blocking',
        'Zero-day threat identification',
        'Detailed security reports'
      ]
    },
    {
      icon: Activity,
      title: 'Autonomous System Maintenance',
      description: 'Caretaker agent monitors system health 24/7, predicting failures before they occur and executing preventive maintenance.',
      features: [
        'Disk space monitoring and cleanup',
        'CPU and memory optimization',
        'Service health tracking',
        'Predictive maintenance alerts'
      ]
    },
    {
      icon: Wifi,
      title: 'Intelligent Network Discovery',
      description: 'Network Agent automatically discovers and tracks all devices on your network with accurate naming and status monitoring.',
      features: [
        'Automatic device discovery',
        'Hostname resolution',
        'Connection history tracking',
        '95+ devices monitored simultaneously'
      ]
    },
    {
      icon: Bell,
      title: 'Context-Aware Notifications',
      description: 'Herald agent delivers the right information at the right time through Discord, SMS, or email based on severity and preferences.',
      features: [
        'Multi-channel routing',
        'Severity-based filtering',
        'Quiet hours support',
        'Real-time delivery under 30 seconds'
      ]
    },
    {
      icon: Zap,
      title: 'GPU-Accelerated AI',
      description: 'Local LLM processing with 50x faster response times. Chat, analyze, and control your system using natural language.',
      features: [
        'Sub-second AI responses',
        'Complete privacy (no cloud)',
        'Natural language control',
        'Learning and adaptation'
      ]
    },
    {
      icon: Lock,
      title: 'Privacy-First Architecture',
      description: 'All data processing happens locally. No telemetry, no cloud dependencies, complete control over your information.',
      features: [
        'Zero data leaves your network',
        'Open source components',
        'Self-hosted infrastructure',
        'Full transparency'
      ]
    }
  ];

  return (
    <div className="min-h-screen pt-24 pb-16 px-4">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-16"
        >
          <h1 className="text-5xl md:text-6xl font-bold text-green-400 mb-4">
            Solutions for Modern Networks
          </h1>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto">
            Multi-agent AI system that monitors, protects, and maintains your infrastructure autonomously
          </p>
        </motion.div>

        {/* Solutions Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {solutions.map((solution, index) => (
            <motion.div
              key={solution.title}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
              className="bg-black/50 border border-green-500/20 rounded-lg p-6 hover:border-green-500/40 transition-colors"
            >
              <div className="flex items-start space-x-4">
                <div className="flex-shrink-0">
                  <solution.icon className="w-12 h-12 text-green-400" />
                </div>
                <div className="flex-1">
                  <h3 className="text-2xl font-bold text-green-400 mb-2">
                    {solution.title}
                  </h3>
                  <p className="text-gray-300 mb-4">
                    {solution.description}
                  </p>
                  <ul className="space-y-2">
                    {solution.features.map((feature) => (
                      <li key={feature} className="flex items-center text-gray-400">
                        <span className="text-green-400 mr-2">▸</span>
                        {feature}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        {/* CTA */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.6 }}
          className="mt-16 text-center"
        >
          <a
            href="https://macmind.macpherson.ai"
            className="inline-block px-8 py-4 bg-green-500 text-black font-bold rounded-lg hover:bg-green-400 transition-colors text-lg"
          >
            See MacMind in Action
          </a>
        </motion.div>
      </div>
    </div>
  );
};

export default SolutionsPage;
